import type { CalendarProviderAccount } from '../store/schema/CalendarProviderAccount';
import type { CalendarProviderCalendar } from '../store/schema/CalendarProviderCalendar';
import { CalendarProviderType } from '../store/schema/CalendarProviderType';
import type { SubscribeCalendarRequest } from '../types/SubscribeCalendarRequest';
import type { TeamSnapSubscribeCalendarRequest } from '../types/TeamSnapSubscribeCalendarRequest';
import { TeamSnapCalendarType } from '../store/schema/TeamSnapCalendarType';
import { action } from 'satcheljs';
import { addDatapointConfig, CustomData } from 'owa-analytics-actions';

export const preloadAndInitializeCalendarProviderAccounts = action(
    'PRELOAD_AND_INITIALIZE_CALENDAR_PROVIDER_ACCOUNTS'
);

/**
 * Loading calendar provider accounts
 */
export const loadCalendarProviderAccounts = action('LOAD_CALENDAR_PROVIDER_ACCOUNTS');
export const calendarProviderAccountsLoaded = action(
    'CALENDAR_PROVIDER_ACCOUNTS_LOADED',
    (providerAccounts: CalendarProviderAccount[]) => ({ providerAccounts })
);
export const errorInLoadingCalendarProviderAccounts = action(
    'ERROR_IN_LOADING_CALENDAR_PROVIDER_ACCOUNTS'
);

export const calendarProviderAccountUnauthorized = action(
    'CALENDAR_PROVIDER_ACCOUNT_UNAUTHORIZED',
    (providerAccount: CalendarProviderAccount) => ({ providerAccount })
);

/**
 * Adding calendar provider account
 */
export const addCalendarProviderAccount = action(
    'ADD_CALENDAR_PROVIDER_ACCOUNT',
    (providerType: CalendarProviderType) =>
        addDatapointConfig(
            {
                name: 'addCalendarProviderAccount',
                options: {
                    isCore: true,
                },
                customData: [providerType],
            },
            { providerType }
        )
);
export const calendarProviderAccountAdded = action(
    'CALENDAR_PROVIDER_ACCOUNT_ADDED',
    (providerAccount: CalendarProviderAccount) =>
        addDatapointConfig(
            {
                name: 'calendarProviderAccountAdded',
                options: {
                    isCore: true,
                },
                customData: [providerAccount.providerType],
            },
            { providerAccount }
        )
);
export const errorInAddingCalendarProviderAccount = action(
    'ERROR_IN_ADDING_CALENDAR_PROVIDER_ACCOUNT',
    (providerType: CalendarProviderType) =>
        addDatapointConfig(
            {
                name: 'errorInAddingCalendarProviderAccount',
                options: {
                    isCore: true,
                },
                customData: [providerType],
            },
            { providerType }
        )
);

/**
 * Reuath calendar provider account
 */
export const reauthCalendarProviderAccount = action(
    'REAUTH_CALENDAR_PROVIDER_ACCOUNT',
    (providerType: CalendarProviderType) =>
        addDatapointConfig(
            {
                name: 'reauthCalendarProviderAccount',
                options: {
                    isCore: true,
                },
                customData: [providerType],
            },
            { providerType }
        )
);

export const reauthCalendarProviderAccountComplete = action(
    'REAUTH_CALENDAR_PROVIDER_ACCOUNT_COMPLETE',
    (providerAccount: CalendarProviderAccount) => ({ providerAccount })
);
export const errorWhenReauthCalendarProviderAccount = action(
    'ERROR_WHEN_REAUTH_CALENDAR_PROVIDER_ACCOUNT',
    (providerType: CalendarProviderType) => ({ providerType })
);

/**
 * Remove calendar provider account
 */
export const removeCalendarProviderAccount = action(
    'REMOVE_CALENDAR_PROVIDER_ACCOUNT',
    (providerType: CalendarProviderType) =>
        addDatapointConfig(
            {
                name: 'removeCalendarProviderAccount',
                options: {
                    isCore: true,
                },
                customData: [providerType],
            },
            { providerType }
        )
);
export const calendarProviderAccountRemoved = action(
    'CALENDAR_PROVIDER_ACCOUNT_REMOVED',
    (providerType: CalendarProviderType, calendarProviderAccountId: string) => ({
        providerType,
        calendarProviderAccountId,
    })
);
export const errorInRemovingCalendarProviderAccount = action(
    'ERROR_IN_REMOVING_CALENDAR_PROVIDER_ACCOUNT',
    (providerType: CalendarProviderType) => ({
        providerType,
    })
);

/**
 * Loading calendars for a calendar provider account
 */
export const loadCalendarsForCalendarProvider = action(
    'LOAD_CALENDARS_FOR_CALENDAR_PROVIDER',
    (providerType: CalendarProviderType) => ({ providerType })
);
export const calendarsForCalendarProviderLoaded = action(
    'CALENDARS_FOR_CALENDAR_PROVIDER_LOADED',
    (
        providerType: CalendarProviderType,
        providerId: string,
        calendars: CalendarProviderCalendar[]
    ) => ({ providerType, providerId, calendars })
);
export const errorInLoadingCalendarsForCalendarProvider = action(
    'ERROR_IN_LOADING_CALENDARS_FOR_CALENDAR_PROVIDER',
    (providerType: CalendarProviderType) => ({ providerType })
);

/**
 * Batch subscribe and unsubscribe calendars
 */
function updateCalendarSubscriptionsOfCalendarProviderRequest(
    providerType: CalendarProviderType.TeamSnap,
    subscribeRequests: TeamSnapSubscribeCalendarRequest[],
    unsubscribeCalendarIds: string[]
): {
    providerType: CalendarProviderType.TeamSnap;
    subscribeRequests: TeamSnapSubscribeCalendarRequest[];
    unsubscribeCalendarIds: string[];
};
function updateCalendarSubscriptionsOfCalendarProviderRequest(
    providerType: Exclude<CalendarProviderType, CalendarProviderType.TeamSnap>,
    subscribeRequests: SubscribeCalendarRequest[],
    unsubscribeCalendarIds: string[]
): {
    providerType: Exclude<CalendarProviderType, CalendarProviderType.TeamSnap>;
    subscribeRequests: SubscribeCalendarRequest[];
    unsubscribeCalendarIds: string[];
};
function updateCalendarSubscriptionsOfCalendarProviderRequest(
    providerType: CalendarProviderType,
    subscribeRequests: SubscribeCalendarRequest[],
    unsubscribeCalendarIds: string[]
): {
    providerType: CalendarProviderType;
    subscribeRequests: SubscribeCalendarRequest[];
    unsubscribeCalendarIds: string[];
} {
    const customData: CustomData = [
        providerType,
        subscribeRequests.length,
        unsubscribeCalendarIds.length,
    ];

    if (providerType === CalendarProviderType.TeamSnap) {
        customData.push(
            (subscribeRequests as TeamSnapSubscribeCalendarRequest[]).filter(
                req => req.calendarType === TeamSnapCalendarType.All
            ).length
        );
        customData.push(
            (subscribeRequests as TeamSnapSubscribeCalendarRequest[]).filter(
                req => req.calendarType === TeamSnapCalendarType.Games
            ).length
        );
    } else {
        // To keep the array of custom data consistent we add 0
        // when the provider is not TeamSnap
        customData.push(...[0, 0]);
    }

    return addDatapointConfig(
        {
            name: 'updateCalendarSubscriptionsOfCalendarProvider',
            customData,
        },
        { providerType, subscribeRequests, unsubscribeCalendarIds }
    ) as any;
}
export const updateCalendarSubscriptionsOfCalendarProvider = action(
    'UPDATE_CALENDAR_SUBSCRIPTIONS_OF_CALENDAR_PROVIDER',
    updateCalendarSubscriptionsOfCalendarProviderRequest
);

export const calendarSubscriptionsUpdatedOfCalendarProvider = action(
    'CALENDAR_SUBSCRIPTIONS_UPDATED_OF_CALENDAR_PROVIDER',
    (
        providerType: CalendarProviderType,
        subscribeCalendarIds: string[],
        unsubscribeCalendarIds: string[]
    ) => ({ providerType, subscribeCalendarIds, unsubscribeCalendarIds })
);

export const errorWhenUpdatingCalendarSubscriptionsOfCalendarProvider = action(
    'ERROR_WHEN_UPDATING_CALENDAR_SUBSCRIPTIONS_OF_CALENDAR_PROVIDER',
    (
        providerType: CalendarProviderType,
        failedToSubscribeCalendarIds: string[],
        failedToUnsubscribeCalendarIds: string[]
    ) => ({
        providerType,
        failedToSubscribeCalendarIds,
        failedToUnsubscribeCalendarIds,
    })
);
